import React, { useState, useRef, useEffect } from 'react';
import { FolderIcon, FolderPlusIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from '../icons';
import { useFolders } from '../../hooks/useFolder';

const FolderSelect = ({
  value,
  onChange,
  options = [],
  onNewFolder,
  className = "",
  placeholder = "Select folder..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const submittingRef = useRef(false);

  const { createFolder } = useFolders();

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm) {
      setFilteredOptions(options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleNewFolderClick = () => {
    setIsCreatingNew(true);
    setNewFolderName('');
  };

  const handleNewFolderSubmit = async () => {
    if (submittingRef.current) return;
    const name = newFolderName.trim();
    if (!name) {
      setIsCreatingNew(false);
      return;
    }
    submittingRef.current = true;
    try {
      const folder = await createFolder(name);
      const newOption = {
        value: folder.id,
        label: folder.name,
        color: 'rgba(74, 186, 98, 1)',
        fill: 'rgba(74, 186, 98, 0.2)'
      };
      onChange(newOption);
    } catch {
      // error already toasted by hook
    }
    submittingRef.current = false;
    setIsCreatingNew(false);
    setNewFolderName('');
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleNewFolderKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNewFolderSubmit();
    } else if (e.key === 'Escape') {
      setIsCreatingNew(false);
      setNewFolderName('');
    }
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`relative ${className}`}>
      {/* Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-1 pr-3 h-[38px] bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-xl hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-colors"
      >
        <div className="w-8 h-8 flex items-center justify-center">
          <FolderIcon 
            size={16} 
            color={selectedOption?.color || "rgba(201, 148, 1, 1)"} 
            fill={selectedOption?.fill || "rgba(201, 148, 1, 0.2)"} 
          />
        </div>
        <span className="text-sm mr-2 font-medium text-[#121212] dark:text-[#D0D0D0]">
          {selectedOption?.label || placeholder}
        </span>
        {isOpen ? (
          <ChevronUpIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
        ) : (
          <ChevronDownIcon size={12} className="text-[#7B7B7B] dark:text-[#848484]" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 mb-1 w-[176px] bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-xl shadow-lg z-50"
          style={{
            boxShadow: '0 -10px 21px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Search Input */}
          <div className="p-2">
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#7B7B7B] dark:text-[#848484]" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search folder..."
                className="w-full pl-9 pr-3 py-2 text-xs bg-[#FFFFFF] dark:bg-[#121212] border border-[#ECECEC] dark:border-[#202020] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[#121212] dark:text-[#D0D0D0]"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="px-2 pb-2 space-y-1">
            {/* New Folder Option */}
            {!isCreatingNew && (
              <button
                onClick={handleNewFolderClick}
                className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-[#F1F1F1] dark:hover:bg-[#252525] transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <FolderPlusIcon size={16} className="text-[#7B7B7B] dark:text-[#848484]" />
                </div>
                <span className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0]">New Folder</span>
              </button>
            )}

            {/* Inline name input */}
            {isCreatingNew && (
              <div className="flex items-center space-x-2 px-2 py-2 rounded-lg bg-[#F1F1F1] dark:bg-[#252525]">
                <div className="w-4 h-4 flex items-center justify-center">
                  <FolderIcon size={16} color="rgba(74, 186, 98, 1)" fill="rgba(74, 186, 98, 0.2)" />
                </div>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={handleNewFolderKeyDown}
                  onBlur={handleNewFolderSubmit}
                  placeholder="Untitled Folder"
                  className="flex-1 text-xs font-medium bg-transparent border-none outline-none text-[#121212] dark:text-[#D0D0D0] placeholder:text-[#7B7B7B] dark:placeholder:text-[#848484]"
                  autoFocus
                />
              </div>
            )}

            {/* Folder Options */}
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-[#F1F1F1] dark:hover:bg-[#252525] transition-colors ${
                  option.value === value ? 'bg-[#F7F7F7] dark:bg-[#252525]' : ''
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <FolderIcon 
                    size={16} 
                    color={option.color || "#7B7B7B"} 
                    fill={option.fill || "rgba(123, 123, 123, 0.2)"} 
                  />
                </div>
                <span className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0]">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderSelect;
